<template>
    <div>
      <InventoryHeaderTab />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Stock Transfer</h5>
          <p class="page-sub">
            <span class="batch-pill">{{ store_transfer?.batch_code }}</span>
          </p>
        </div>
        <div class="page-actions">
          <button type="button" @click="$router.go(-1)" class="btn-outline">
            <font-awesome-icon icon="arrow-left" /> Back
          </button>
          <PrintButtonHeader
            :title="'Invoice-' + store_transfer?.batch_code"
            :urlA4="'/view-stock-transfer/'"
            :url58mm="'/print58mm-stock-transfer/'"
            :url80mm="'/print80mm-stock-transfer/'"
            :currentUrl="'Print 80mm Stock Transfer'"
            :data="store_transfer"
            :type="'Stock Transfer'"
          />
        </div>
      </div>
  
      <!-- Skeleton -->
      <div v-if="loading" class="form-card">
        <div class="skeleton-grid">
          <div class="skeleton skeleton--field" v-for="n in 5" :key="n"></div>
        </div>
        <div class="skeleton skeleton--table mt-4"></div>
      </div>
  
      <template v-else>
      <div id="printMe">

        <!-- Summary Card -->
        <div class="form-card mb-4">
          <div class="summary-grid">
            <div class="summary-item">
              <span class="summary-label">Date</span>
              <span class="summary-value">{{ dateTime(store_transfer?.date) }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Transfer No.</span>
              <span class="summary-value">
                <span class="batch-code">{{ store_transfer?.batch_code }}</span>
              </span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Source Store</span>
              <span class="summary-value">
                <span class="store-tag store-tag--source">
                  <font-awesome-icon icon="store" class="me-1" />
                  {{ store_transfer?.store?.store_name }}
                </span>
              </span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Destination Store</span>
              <span class="summary-value">
                <div class="transfer-flow">
                  <font-awesome-icon icon="arrow-right" class="transfer-arrow" />
                  <span class="store-tag store-tag--dest">
                    <font-awesome-icon icon="store" class="me-1" />
                    {{ store_transfer?.destination?.store_name }}
                  </span>
                </div>
              </span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Transferred By</span>
              <span class="summary-value">
                <div class="user-cell">
                  <div class="user-avatar">{{ store_transfer?.user?.fullname?.charAt(0) }}</div>
                  {{ store_transfer?.user?.fullname }}
                </div>
              </span>
            </div>
          </div>
        </div>
  
        <!-- Items Table Card -->
        <div class="form-card">
          <div class="section-header">
            <div class="section-icon section-icon--teal">
              <font-awesome-icon icon="exchange-alt" />
            </div>
            <div>
              <p class="section-title">Transferred Products</p>
              <p class="section-sub">{{ issue_stock_item.length }} product(s) in this transfer</p>
            </div>
          </div>

          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Product</th>
                  <th>Variant / UOM</th>
                  <th>Quantity</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in issue_stock_item" :key="index">
                  <td class="text-muted-cell">{{ index + 1 }}</td>
                  <td>
                    <div class="product-cell">
                      <div class="product-avatar"><font-awesome-icon icon="box" /></div>
                      <span class="product-name">{{ item?.product?.product_name }}</span>
                    </div>
                  </td>
                  <td class="text-muted-cell">
                    {{ item?.variant?.variant_name ?? item?.product?.unit_of_measurement }}
                  </td>
                  <td>
                    <span class="qty-badge">{{ item?.quantity }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div><!-- /#printMe -->
      </template>
    </div>
  </template>
  
  <script>
  import InventoryHeaderTab from './component/inventoryheadertab.vue';
  import { useToast } from 'vue-toastification';
  import axios from 'axios';
  import { reusableMixin } from '../../../lib/reusableMixin';
  
  export default {
    components: { InventoryHeaderTab },
    mixins: [reusableMixin],
  
    data() {
      return {
        toast: useToast(),
        store_transfer:   null,
        issue_stock_item: [],
        loading:          false,
      };
    },
  
    created() { this.getStockTransfer(); },
  
    methods: {
      getStockTransfer() {
        this.loading = true;
        axios.get(`/inventory/issue-stock/${this.$route.params.id}`)
          .then(response => {
            this.loading          = false;
            this.store_transfer   = response.data.data;
            this.issue_stock_item = response.data.data.issue_stock_item;
          })
          .catch(() => {
            this.loading = false;
            this.toast.error('Something went wrong');
          });
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .page-sub     { margin:0; }
  .page-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  
  /* ── Skeleton ── */
  .skeleton-grid  { display:grid; grid-template-columns:repeat(5,1fr); gap:16px; }
  .skeleton       { border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  .skeleton--field { height:52px; }
  .skeleton--table { height:200px; }
  .mt-4 { margin-top:16px; }
  
  /* ── Form Card ── */
  .form-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
  .mb-4      { margin-bottom:16px; }
  
  /* ── Summary Grid ── */
  .summary-grid  { display:grid; grid-template-columns:repeat(5,1fr); gap:20px; }
  .summary-item  { display:flex; flex-direction:column; gap:8px; }
  .summary-label { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
  .summary-value { font-size:13.5px; font-weight:600; color:#1a2b45; }
  
  /* ── Section Header ── */
  .section-header { display:flex; align-items:center; gap:14px; margin-bottom:18px; }
  .section-icon   { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
  .section-icon--teal { background:rgba(13,148,136,.1); color:#0d9488; }
  .section-title  { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .section-sub    { font-size:12px; color:#7a90b0; margin:0; }
  
  /* ── Batch / Store Tags ── */
  .batch-pill { display:inline-block; background:#edf4ff; color:#004AAD; border:1px solid #c5d6f5; border-radius:20px; padding:2px 12px; font-size:13px; font-weight:600; }
  .batch-code { font-family:monospace; font-size:13px; font-weight:700; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:3px 10px; }
  
  .store-tag        { display:inline-flex; align-items:center; border-radius:6px; padding:4px 10px; font-size:13px; font-weight:600; border:1px solid transparent; }
  .store-tag--source { background:#edf4ff; color:#004AAD; border-color:#c5d6f5; }
  .store-tag--dest   { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  
  .transfer-flow  { display:flex; align-items:center; gap:8px; }
  .transfer-arrow { font-size:12px; color:#aab8cc; }
  
  /* ── User Cell ── */
  .user-cell   { display:flex; align-items:center; gap:8px; font-size:13.5px; font-weight:600; color:#1a2b45; }
  .user-avatar { width:28px; height:28px; border-radius:50%; background:#004AAD; color:#fff; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; flex-shrink:0; text-transform:uppercase; }
  
  /* ── Table ── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:11px 14px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:12px 14px; font-size:13.5px; vertical-align:middle; }
  .text-muted-cell { color:#4a5e78; }
  
  /* ── Product Cell ── */
  .product-cell   { display:flex; align-items:center; gap:10px; }
  .product-avatar { width:32px; height:32px; border-radius:8px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:13px; flex-shrink:0; }
  .product-name   { font-weight:600; color:#1a2b45; }
  
  /* ── Qty Badge ── */
  .qty-badge { display:inline-block; background:#edf4ff; color:#004AAD; border-radius:20px; padding:2px 10px; font-size:12.5px; font-weight:600; }
  
  /* ── Buttons ── */
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; text-decoration:none; }
  .btn-outline:hover { background:#f4f7fb; color:#1a2b45; }
  
  /* ── Responsive ── */
  @media(max-width:1200px) { .summary-grid { grid-template-columns:repeat(3,1fr); } .skeleton-grid { grid-template-columns:repeat(3,1fr); } }
  @media(max-width:768px)  { .summary-grid { grid-template-columns:repeat(2,1fr); } .skeleton-grid { grid-template-columns:repeat(2,1fr); } }
  @media(max-width:576px)  {
    .summary-grid,.skeleton-grid { grid-template-columns:1fr; }
    .form-card { padding:16px; }
    .page-header,.page-actions { flex-direction:column; align-items:stretch; }
    .btn-outline { width:100%; justify-content:center; }
  }
  </style>